import { Badge, Box, Divider, Flex, Group, Stack, Table, Text, Title } from "@mantine/core";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { httpPostGraphql } from "shared/api/httpClient";
import { BackButton } from "shared/components/global/BackButton";
import { Content } from "shared/components/global/Content";
import { Section, SectionProvider } from "shared/components/global/Section";
import { MapBase } from "shared/components/map/MapBase";
import { MapFeatureOverlay } from "shared/components/map/MapFeatureOverlay";
import { useIsMobile } from "shared/hooks/useIsMobile";
import { arr } from "shared/utils/array";
import { fail, titlecase, to } from "shared/utils/fns";
import { maybe } from "shared/utils/maybe";
import { zx } from "shared/utils/zx";
import { graphql } from "src/gql";
import { IpawsAlertStatus, IpawsCapMessageAddressNode, IpawsCertificateType, IpawsChannel } from "src/gql/graphql";
import { queryClient } from "src/queryClient";
import { match } from "ts-pattern";

const CapExchRows = (props: { addresses: Pick<IpawsCapMessageAddressNode, "type" | "value">[] }) => {
    const recipientCogIds = props.addresses.map(to("value"));

    if (recipientCogIds.length == 0) return null;
    return (
        <Table.Tr>
            <Table.Th>Exchange COG ID</Table.Th>
            <Table.Td>{recipientCogIds.join(", ")}</Table.Td>
        </Table.Tr>
    );
};

function formatDuration(durationMinutes: number | undefined | null) {
    if (typeof durationMinutes === "undefined" || durationMinutes === null) return "—";

    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;
    return hours > 0
        ? `${hours} hour${hours !== 1 ? "s" : ""} ${minutes} minute${minutes !== 1 ? "s" : ""}`
        : `${minutes} minute${minutes !== 1 ? "s" : ""}`;
}

const Page = () => {
    const { alertId } = Route.useParams();
    const { data } = useSuspenseQuery(queries.details(alertId));
    const isMobile = useIsMobile();
    const tableColumnWidth = isMobile ? 120 : 200;

    const enInfo = data.capMessage.infos.find((it) => it.language == "en-US") ?? fail("English info is required to view alert details.");
    const esInfo = data.capMessage.infos.find((it) => it.language == "es-US");

    const evtCode = enInfo.eventCodes.at(0) ?? fail("At least one event code is required.");
    const evtCodeDesc =
        data.certificate.channels.flatMap((ch) => ch.eventCodes).find((ec) => ec.id == evtCode)?.description ??
        fail("Failed to find event code in cert.");

    const channels = arr(Object.values(IpawsChannel))
        .except(...enInfo.blockedChannels)
        .take();

    return (
        <Content paper>
            <Content.Heading>
                <BackButton nav={{ to: "/ipaws/alerts" }} />
                <Title order={3}>IPAWS Alert</Title>
                {data.status != IpawsAlertStatus.Cancel && new Date() < zx.date().parse(enInfo.expires) ? (
                    <Badge color="green">Active</Badge>
                ) : (
                    <Badge color="red">Expired</Badge>
                )}
                {data.status !== IpawsAlertStatus.Published && (
                    <Badge
                        color={match(data.status)
                            .with(IpawsAlertStatus.Cancel, () => "blue")
                            .otherwise(() => "gray")}
                    >
                        {titlecase(data.status)}
                    </Badge>
                )}
            </Content.Heading>
            <SectionProvider>
                <Section label="Details">
                    <Table variant="vertical" withTableBorder layout="fixed">
                        <Table.Tbody>
                            <Table.Tr>
                                <Table.Th w={tableColumnWidth}>Certificate</Table.Th>
                                <Table.Td>
                                    <Group gap="xs">
                                        {data.certificate.name}
                                        {match(data.certificate.type)
                                            .with(IpawsCertificateType.Live, () => <Badge color="red">Live</Badge>)
                                            .with(IpawsCertificateType.Test, () => <Badge color="blue">Test</Badge>)
                                            .exhaustive()}
                                    </Group>
                                </Table.Td>
                            </Table.Tr>
                            <Table.Tr>
                                <Table.Th>Sender</Table.Th>
                                <Table.Td>{data.capMessage.sender}</Table.Td>
                            </Table.Tr>
                            <Table.Tr>
                                <Table.Th>Sent At</Table.Th>
                                <Table.Td>{zx.date().parse(data.capMessage.sent).toLocaleString()}</Table.Td>
                            </Table.Tr>
                            <Table.Tr>
                                <Table.Th>Expires At</Table.Th>
                                <Table.Td>{zx.date().parse(enInfo.expires).toLocaleString()}</Table.Td>
                            </Table.Tr>
                            <Table.Tr>
                                <Table.Th>Duration</Table.Th>
                                <Table.Td>{formatDuration(data.durationMinutes)}</Table.Td>
                            </Table.Tr>
                            <Table.Tr>
                                <Table.Th>Channels</Table.Th>
                                <Table.Td>
                                    <Group gap="xs">
                                        {channels.map((ch) => (
                                            <Badge key={ch} variant="default">
                                                {ch == IpawsChannel.Cmas ? "WEA" : ch}
                                            </Badge>
                                        ))}
                                    </Group>
                                </Table.Td>
                            </Table.Tr>
                            <Table.Tr>
                                <Table.Th>Event Code</Table.Th>
                                <Table.Td>{`${evtCode}: ${evtCodeDesc}`}</Table.Td>
                            </Table.Tr>
                            <Table.Tr>
                                <Table.Th>Status</Table.Th>
                                <Table.Td>{titlecase(data.capMessage.status)}</Table.Td>
                            </Table.Tr>
                            <Table.Tr>
                                <Table.Th>Category</Table.Th>
                                <Table.Td>{enInfo.categories.at(0)}</Table.Td>
                            </Table.Tr>
                            <Table.Tr>
                                <Table.Th>Urgency</Table.Th>
                                <Table.Td>{enInfo.urgency}</Table.Td>
                            </Table.Tr>
                            <Table.Tr>
                                <Table.Th>Severity</Table.Th>
                                <Table.Td>{enInfo.severity}</Table.Td>
                            </Table.Tr>
                            <Table.Tr>
                                <Table.Th>Certainty</Table.Th>
                                <Table.Td>{enInfo.certainty}</Table.Td>
                            </Table.Tr>
                            {channels.includes(IpawsChannel.Capexch) && (
                                <>
                                    <CapExchRows addresses={data.capMessage.addresses} />
                                    <Table.Tr>
                                        <Table.Th>Notes</Table.Th>
                                        <Table.Td>{data.capMessage.note ?? ""}</Table.Td>
                                    </Table.Tr>
                                </>
                            )}
                        </Table.Tbody>
                    </Table>
                </Section>
                <Section label="Area">
                    {arr(
                        enInfo.areas.map((area, i) => (
                            <Flex key={i} gap="xs" align="flex-start" direction={isMobile ? "column" : "row"}>
                                {area.polygons.length > 0 && (
                                    <Box
                                        flex={isMobile ? undefined : 1}
                                        pos="relative"
                                        style={{ width: isMobile ? "100%" : "200px", height: "200px" }}
                                        tabIndex={-1}
                                    >
                                        <MapBase widget id={"ipaws-alert-form-review-" + i} style={{ borderRadius: "4px" }}>
                                            <MapFeatureOverlay
                                                features={area.polygons.map((p) => ({
                                                    type: "Feature",
                                                    geometry: zx.geo.polygon().parse(p),
                                                    properties: {},
                                                }))}
                                            />
                                        </MapBase>
                                    </Box>
                                )}
                                <Table flex={1} variant="vertical" layout="fixed" withTableBorder>
                                    <Table.Tr>
                                        <Table.Th w={tableColumnWidth}>Name</Table.Th>
                                        <Table.Td>{area.description}</Table.Td>
                                    </Table.Tr>
                                    <Table.Tr>
                                        <Table.Th>FIPS Codes</Table.Th>
                                        <Table.Td>
                                            <Group gap="xs">
                                                {area.geocodes.map((code) => (
                                                    <Badge variant="default" key={code}>
                                                        {code}
                                                    </Badge>
                                                ))}
                                            </Group>
                                        </Table.Td>
                                    </Table.Tr>
                                    <Table.Tr>
                                        <Table.Th>Polygons</Table.Th>
                                        <Table.Td>{area.polygons.length}</Table.Td>
                                    </Table.Tr>
                                </Table>
                            </Flex>
                        )),
                    )
                        .interleave(<Divider />)
                        .take()}
                </Section>
                <Section label="Message Content">
                    <Stack>
                        <div>
                            <Text fw="bold">Headline</Text>
                            <Text size="sm">{enInfo.headline}</Text>
                        </div>

                        <div>
                            <Text fw="bold">Description</Text>
                            <Text size="sm">{enInfo.description}</Text>
                        </div>

                        {!!enInfo.instruction && (
                            <div>
                                <Text fw="bold">Instruction</Text>
                                <Text size="sm">{enInfo.instruction}</Text>
                            </div>
                        )}

                        {maybe(enInfo.resources.find((r) => r.mimeType == "audio/x-ipaws-audio-mp3"))?.take((r) => (
                            <div>
                                <Text fw="bold" mb="xs">
                                    EAS Audio
                                </Text>
                                <audio controls controlsList="nodownload" src={r.uri} />
                            </div>
                        ))}
                    </Stack>
                </Section>

                {maybe(enInfo?.cmamText)
                    ?.binding("shortText")
                    .bind("longText", enInfo?.cmamLongText)
                    ?.take((wea) => (
                        <Section label="WEA">
                            <Table variant="vertical" withTableBorder>
                                <Table.Tbody>
                                    <Table.Tr>
                                        <Table.Th w={tableColumnWidth}>WEA Handling</Table.Th>
                                        <Table.Td>{titlecase(enInfo.weaHandling?.replaceAll("_", " ") ?? "")}</Table.Td>
                                    </Table.Tr>
                                </Table.Tbody>
                            </Table>

                            <div>
                                <Text fw="bold">WEA Short Text</Text>
                                <Text size="sm">{wea.shortText}</Text>
                            </div>

                            <div>
                                <Text fw="bold">WEA Long Text</Text>
                                <Text size="sm">{wea.longText}</Text>
                            </div>
                        </Section>
                    ))}

                {maybe(esInfo)?.take((it) => (
                    <Section label="Message Content (Spanish)">
                        <Stack>
                            <div>
                                <Text fw="bold">Headline</Text>
                                <Text size="sm">{it.headline}</Text>
                            </div>

                            <div>
                                <Text fw="bold">Description</Text>
                                <Text size="sm">{it.description}</Text>
                            </div>

                            {maybe(it.instruction)
                                ?.if((it) => it.trim().length > 0)
                                ?.take((instr) => (
                                    <div>
                                        <Text fw="bold">Instruction</Text>
                                        <Text size="sm">{instr}</Text>
                                    </div>
                                ))}
                        </Stack>
                    </Section>
                ))}

                {maybe(esInfo?.cmamText)
                    ?.binding("shortText")
                    .bind("longText", esInfo?.cmamLongText)
                    ?.take((wea) => (
                        <Section label="WEA (Spanish)">
                            <Table variant="vertical" withTableBorder>
                                <Table.Tbody>
                                    <Table.Tr>
                                        <Table.Th w={tableColumnWidth}>WEA Handling</Table.Th>
                                        <Table.Td>{titlecase(esInfo?.weaHandling?.replaceAll("_", " ") ?? "")}</Table.Td>
                                    </Table.Tr>
                                </Table.Tbody>
                            </Table>

                            <div>
                                <Text fw="bold">WEA Short Text</Text>
                                <Text size="sm">{wea.shortText}</Text>
                            </div>

                            <div>
                                <Text fw="bold">WEA Long Text</Text>
                                <Text size="sm">{wea.longText}</Text>
                            </div>
                        </Section>
                    ))}

                {maybe(data.response)?.take((response) => (
                    <Section label="IPAWS Service Response">
                        <Table striped withTableBorder>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th />
                                    <Table.Th>Channel</Table.Th>
                                    <Table.Th>Code</Table.Th>
                                    <Table.Th>Description</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {response.map((each, idx) => (
                                    <Table.Tr key={idx}>
                                        <Table.Td style={{ textAlign: "center" }}>
                                            {each.success ? (
                                                <Badge variant="filled" color="green">
                                                    ok
                                                </Badge>
                                            ) : (
                                                <Badge variant="filled" color="red">
                                                    Error
                                                </Badge>
                                            )}
                                        </Table.Td>
                                        <Table.Td>
                                            <Badge variant="default">{each.channel}</Badge>
                                        </Table.Td>
                                        <Table.Td>{each.statusId}</Table.Td>
                                        <Table.Td>{each.status}</Table.Td>
                                    </Table.Tr>
                                ))}
                                {response.length == 0 && (
                                    <Table.Tr>
                                        <Table.Td colSpan={4} ta="center">
                                            <Text size="xs" c="dimmed">
                                                No IPAWS Service Responses
                                            </Text>
                                        </Table.Td>
                                    </Table.Tr>
                                )}
                            </Table.Tbody>
                        </Table>
                    </Section>
                ))}
            </SectionProvider>
        </Content>
    );
};

const queries = {
    details: (id: string) =>
        queryOptions({
            queryKey: ["ipaws", "alert", id],
            queryFn: () =>
                httpPostGraphql(
                    graphql(`
                        query IpawsAlertDetails($id: UUID!) {
                            ipawsAlert(id: $id) {
                                id
                                serviceError
                                status
                                certificate {
                                    id
                                    name
                                    type
                                    channels {
                                        eventCodes {
                                            id
                                            description
                                        }
                                    }
                                }
                                durationMinutes
                                capMessage {
                                    ...IpawsCapMessageDetails
                                }
                                response {
                                    channel
                                    success
                                    statusId
                                    status
                                }
                            }
                        }
                    `),
                    { id },
                ),
            select: to("ipawsAlert"),
        }),
};

/** @public */
export const Route = createFileRoute("/_auth/ipaws/alerts/$alertId")({
    component: Page,
    async beforeLoad({ params: { alertId } }) {
        await queryClient.prefetchQuery(queries.details(alertId));
    },
});
